/**
 * Admin app bootstrap — leaner than portal's main.tsx.
 *
 * Differences from portal:
 *   - No debug-utility imports (portal's main.tsx attaches ~10 console
 *     helpers like syncApifyRun, debugGeorgiaRecalls — those are dealer-
 *     facing dev tools, not admin-relevant).
 *   - No console-override (admin is for staff; full console output helps).
 *   - No setQueryClientInstance (admin doesn't need clearZoneMapCaches's
 *     query-client hook — that utility is portal-side dev tooling).
 *
 * Same shape: validate env → hydrate session (deferred) → mount React tree
 * with QueryClientProvider, MantineProvider, App.
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { MantineProvider, createTheme } from '@mantine/core';

import App from './App';
import { Toaster } from '@vehix/portal/components/ui/toaster';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import './styles/globals.css';

import { validateEnvironment } from './config/environment';

try {
  validateEnvironment();
} catch (error) {
  // Render a minimal config-error page rather than the app.
  const rootElement = document.getElementById('root');
  if (rootElement) {
    ReactDOM.createRoot(rootElement).render(
      <React.StrictMode>
        <div
          style={{
            padding: '2rem',
            maxWidth: '800px',
            margin: '2rem auto',
            fontFamily: 'system-ui, sans-serif',
            backgroundColor: '#fee2e2',
            border: '1px solid #ef4444',
            borderRadius: '8px'
          }}
        >
          <h1 style={{ color: '#dc2626', marginBottom: '1rem' }}>Configuration Error</h1>
          <p style={{ marginBottom: '1rem' }}>
            The admin app failed to start due to missing or invalid environment configuration:
          </p>
          <pre
            style={{
              backgroundColor: '#f3f4f6',
              padding: '1rem',
              borderRadius: '4px',
              overflow: 'auto',
              fontSize: '0.875rem'
            }}
          >
            {error instanceof Error ? error.message : String(error)}
          </pre>
        </div>
      </React.StrictMode>
    );
  }
  throw error;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: (failureCount, error) => {
        if (error instanceof Error) {
          if (
            error.message?.includes('403') ||
            error.message?.includes('Forbidden') ||
            error.message?.includes('permission denied')
          ) {
            return false;
          }
          if ('status' in error && typeof error.status === 'number') {
            if (error.status >= 400 && error.status < 500) return false;
          }
        }
        return failureCount < 3;
      }
    },
    mutations: { retry: false }
  }
});

// Mantine theme — matches portal's brand colors so shared admin components render identically.
const mantineTheme = createTheme({
  primaryColor: 'brand',
  defaultRadius: 'md',
  fontFamily: 'system-ui, -apple-system, sans-serif',
  colors: {
    brand: [
      '#fef7ee',
      '#fde6d0',
      '#fcd5b1',
      '#fac392',
      '#f9b273',
      '#d4854a',
      '#b6703f',
      '#985b34',
      '#7a4629',
      '#5c311f'
    ]
  }
});

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Root element not found');

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <MantineProvider theme={mantineTheme}>
      <QueryClientProvider client={queryClient}>
        <App />
        <Toaster />
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </MantineProvider>
  </React.StrictMode>
);
